import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useStore } from "../../../../stores/StoreContext";

import "./PermissionsTab.scss";

import { AppRole, SecurityGroupRoleAssignmentsReadModel, StringReferenceReadModel } from "../../../../../@api";

import { translationKeys } from "../../../../models/app-translation-keys";
import { useAppRolesService } from "../../../../services/ServiceHooks/appRolesHook";
import { TextMarkdown } from "../../../Shared/Components";
import AddSecurityGroupButton from "./AddSecurityGroupButton/AddSecurityGroupButton";
import SecurityGroupsTable from "./SecurityGroupsTable";

export interface ISecurityGroupRoleAssignments extends SecurityGroupRoleAssignmentsReadModel {
  assignedRolesForFilter: string;
}

const PermissionsTab: FunctionComponent = () => {
  const { permissionStore } = useStore();
  const appRolesService = useAppRolesService();
  const { t } = useTranslation();

  const [securityGroups, setSecurityGroups] = useState<ISecurityGroupRoleAssignments[]>([]);
  const [roles, setRoles] = useState<StringReferenceReadModel[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(true);

  const requestPermissions = async () => {
    setIsLoading(true);

    const securityGroupsResponse = await appRolesService.getActiveRoleAssignments();

    const securityGroupsWithAssignedRoles: ISecurityGroupRoleAssignments[] =
      securityGroupsResponse?.assignments?.entities?.map(entity => ({
        ...entity,
        assignedRolesForFilter:
          entity.assignedRoleIds
            .map(roleId => securityGroupsResponse.roles.find(role => role.id === roleId)?.displayName)
            .join(", ") || "",
      })) || [];
    setSecurityGroups(securityGroupsWithAssignedRoles);
    setRoles(securityGroupsResponse?.roles ?? []);

    setIsLoading(false);
  };

  useEffect(() => {
    if (!securityGroups?.length && permissionStore.isPermissionAdmin()) {
      requestPermissions();
    }
  }, []);

  const securityGroupTableMemo = useMemo(() => {
    return (
      <SecurityGroupsTable
        securityGroups={securityGroups}
        roles={roles}
        isLoading={isLoading}
        onReloadTable={requestPermissions}
      />
    );
  }, [securityGroups, roles, isLoading]);

  return (
    <div className="permissions-tab">
      <div className="terms-of-use-upload-header">
        <h1>{t(translationKeys.adminArea.appRoles.tabHeader)}</h1>

        <TextMarkdown>{t(translationKeys.adminArea.appRoles.tabDescriptionMarkdown)}</TextMarkdown>
      </div>

      <div className="flex flex-column justify-content-end align-items-end gap-2">
        <div style={{ width: "fit-content" }}>
          <AddSecurityGroupButton
            existingSecurityGroupIds={securityGroups.map(group => group.securityGroup.id!)}
            onAddedSecurityGroup={requestPermissions}
          />
        </div>

        <div style={{ width: "100%" }}>{securityGroupTableMemo}</div>
      </div>
    </div>
  );
};

export default PermissionsTab;
